/**
 * Quick-add an UNSIGNED promoter from the event wizard / event edit (migration 1712, owner 2026-11-04).
 *
 * Minimal fields: name, state, optional contact. "Check and add" asks the server first: an exact duplicate (same name
 * and state) is refused with "use it"; similar organizations are listed and the rep must tick "none of these is the
 * promoter" before "Add anyway". The new organization is a promoter prospect with no portal access and no partner
 * agreement; when it has no CTA, the event's CTA uses the state's No Reseller codes.
 */
import React, { useState } from "react";
import Modal from "../ui/Modal";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";
import { US_STATES } from "../../types/event.types";
import {
  createUnsignedPromoter,
  createUnsignedPromoterMessage,
  type SimilarOrganization,
} from "../../services/unsignedPromoter.service";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialName?: string;
  initialState?: string | null;
  /** Called with the promoter to record on the event (new, or an existing one the rep picked). */
  onSelected: (id: string, name: string) => void;
}

const typeLabel = (t: string | null) =>
  t ? t.replace(/_/g, " ") : "organization";

const AddUnsignedPromoterModal: React.FC<Props> = ({
  isOpen,
  onClose,
  initialName = "",
  initialState = null,
  onSelected,
}) => {
  const [name, setName] = useState(initialName);
  const [state, setState] = useState((initialState ?? "").toUpperCase());
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [candidates, setCandidates] = useState<SimilarOrganization[] | null>(
    null,
  );
  const [exact, setExact] = useState<SimilarOrganization | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Any change to name / state invalidates the duplicate check the rep confirmed.
  const resetCheck = () => {
    setCandidates(null);
    setExact(null);
    setConfirmed(false);
    setError(null);
  };

  const submit = async (confirm: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await createUnsignedPromoter({
        name,
        state,
        contactName,
        contactEmail,
        contactPhone,
        confirmNoDuplicate: confirm,
      });
      if (r.status === "created") {
        onSelected(r.organization.id, r.organization.name);
        onClose();
      } else if (r.status === "exists") {
        setExact(r.organization);
        setCandidates(r.candidates);
      } else if (r.status === "possible_duplicates") {
        setCandidates(r.candidates);
      } else {
        setError(createUnsignedPromoterMessage(r.status));
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "The promoter couldn't be added.",
      );
    } finally {
      setBusy(false);
    }
  };

  const pick = (c: SimilarOrganization) => {
    onSelected(c.id, c.name);
    onClose();
  };

  const canSubmit = name.trim().length >= 3 && /^[A-Z]{2}$/.test(state);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add an unsigned promoter"
      size="lg"
    >
      <div className="space-y-4" data-testid="add-unsigned-promoter">
        <p className="text-sm text-gray-600">
          Use this when the show&apos;s promoter isn&apos;t in the list. It is
          added as an <strong>unsigned promoter</strong>: not a partner, no
          portal access, no partner agreement. If it has no promo code of its
          own, the event&apos;s QR code uses the state&apos;s No Reseller code.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="sm:col-span-2 text-sm">
            <span className="block font-medium text-gray-700">
              Promoter name <span className="text-red-500">*</span>
            </span>
            <input
              onKeyDown={preventEnterSubmit}
              type="text"
              value={name}
              maxLength={120}
              onChange={(e) => {
                setName(e.target.value);
                resetCheck();
              }}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              data-testid="unsigned-promoter-name"
            />
          </label>
          <label className="text-sm">
            <span className="block font-medium text-gray-700">
              State <span className="text-red-500">*</span>
            </span>
            <select
              value={state}
              onChange={(e) => {
                setState(e.target.value);
                resetCheck();
              }}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              data-testid="unsigned-promoter-state"
            >
              <option value="">Choose…</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <fieldset className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <legend className="text-sm font-medium text-gray-700 mb-1">
            Contact (optional)
          </legend>
          <input
            onKeyDown={preventEnterSubmit}
            type="text"
            placeholder="Name"
            value={contactName}
            maxLength={120}
            onChange={(e) => setContactName(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <input
            onKeyDown={preventEnterSubmit}
            type="email"
            placeholder="Email"
            value={contactEmail}
            maxLength={200}
            onChange={(e) => setContactEmail(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <input
            onKeyDown={preventEnterSubmit}
            type="tel"
            placeholder="Phone"
            value={contactPhone}
            maxLength={40}
            onChange={(e) => setContactPhone(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </fieldset>

        {exact && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="unsigned-promoter-exists"
          >
            <p>
              <strong>{exact.name}</strong>
              {exact.state ? ` (${exact.state})` : ""} is already a promoter.
              Use it instead of adding it again.
            </p>
            <button
              type="button"
              onClick={() => pick(exact)}
              className="mt-2 px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm"
            >
              Use {exact.name}
            </button>
          </div>
        )}

        {!exact && candidates && candidates.length > 0 && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2"
            data-testid="unsigned-promoter-duplicates"
          >
            <p className="font-medium">
              These existing organizations look similar. Is one of them this
              promoter?
            </p>
            <ul className="divide-y divide-amber-200">
              {candidates.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-2 py-1.5"
                >
                  <span>
                    <strong>{c.name}</strong>
                    {c.state ? ` · ${c.state}` : ""} ·{" "}
                    {typeLabel(c.organization_type)}
                    {c.unsigned ? " · unsigned" : ""}
                    {c.status && c.status !== "active" ? ` · ${c.status}` : ""}
                  </span>
                  {c.organization_type === "promoter" ? (
                    <button
                      type="button"
                      onClick={() => pick(c)}
                      className="shrink-0 px-2 py-1 rounded border border-amber-400 bg-white text-xs"
                    >
                      Use this one
                    </button>
                  ) : (
                    <span className="shrink-0 text-xs text-amber-800">
                      not a promoter: ask an admin to change its type
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                onKeyDown={preventEnterSubmit}
                className="mt-0.5"
                data-testid="unsigned-promoter-confirm"
              />
              <span>
                None of these is the promoter. Add{" "}
                <strong>{name.trim()}</strong> as a new unsigned promoter.
              </span>
            </label>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm"
          >
            Cancel
          </button>
          {!exact && candidates && candidates.length > 0 ? (
            <button
              type="button"
              disabled={!confirmed || busy}
              onClick={() => submit(true)}
              className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              data-testid="unsigned-promoter-add-anyway"
            >
              {busy ? "Adding…" : "Add anyway"}
            </button>
          ) : (
            !exact && (
              <button
                type="button"
                disabled={!canSubmit || busy}
                onClick={() => submit(false)}
                className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
                data-testid="unsigned-promoter-submit"
              >
                {busy ? "Checking…" : "Check and add"}
              </button>
            )
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddUnsignedPromoterModal;
